import { useCallback, useState } from 'react';
import { A26 } from 'api/U002';
import { FetchError } from 'api/API';

export type UploadConsentFormDoRequestType = {
  sessionId: string;
  lang: string;
  audition_id: string;
  representative_user?: string;
  terms_file: File;
  privacy_notice_file: File;
  specific_use_file: File | undefined;
  audition_terms_file: File | undefined;
  privacy_policy_file: File | undefined;
  evidence_file: File;
  user_id?: string;
};

type UploadConsentFormDoRequest = (param: {
  request: UploadConsentFormDoRequestType;
  onSuccess?: () => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function useUploadConsentForm(): [UploadConsentFormDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const uploadConsentForm = async (
    request: UploadConsentFormDoRequestType,
    cancellation: AbortSignal
  ) => {
    const json = await A26(
      {
        sessionId: request.sessionId,
        lang: request.lang,
        audition_id: request.audition_id,
        representative_user: request.representative_user ?? '',
        specific_use_file_name: request.specific_use_file?.name ?? '',
        terms_file_name: request.terms_file?.name ?? '',
        privacy_notice_file_name: request.privacy_notice_file?.name ?? '',
        evidence_file_name: request.evidence_file.name,
        audition_terms_file_name: request.audition_terms_file?.name ?? '',
        privacy_policy_file_name: request.privacy_policy_file?.name ?? '',
        user_id: request.user_id ?? '',
      },
      cancellation
    );

    // 各ファイルのアップロード
    const uploadResults = await Promise.all([
      fetch(json.terms.url, {
        method: 'PUT',
        body: request.terms_file,
        signal: cancellation,
      }),
      fetch(json.privacy_notice.url, {
        method: 'PUT',
        body: request.privacy_notice_file,
        signal: cancellation,
      }),
      request.specific_use_file
        ? fetch(json.specific_use.url, {
            method: 'PUT',
            body: request.specific_use_file,
            signal: cancellation,
          })
        : Promise.resolve(null),
      request.audition_terms_file
        ? fetch(json.audition_terms.url, {
            method: 'PUT',
            body: request.audition_terms_file,
            signal: cancellation,
          })
        : Promise.resolve(null),
      request.privacy_policy_file
        ? fetch(json.privacy_policy.url, {
            method: 'PUT',
            body: request.privacy_policy_file,
            signal: cancellation,
          })
        : Promise.resolve(null),
      fetch(json.evidence.url, {
        method: 'PUT',
        body: request.evidence_file,
        signal: cancellation,
      }),
    ]);

    const allUploadsSuccessful = uploadResults
      .filter((result): result is Response => result !== null)
      .every((result) => result.ok);

    if (!allUploadsSuccessful) {
      throw new Error('file upload failed');
    }
    return;
  };

  const doRequest = useCallback<UploadConsentFormDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        setLoading(true);
        await uploadConsentForm(request, cancelSignal);
        onSuccess?.();
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [uploadConsentForm, setLoading, setError]
  );

  return [doRequest, loading, err];
}
